import { inject, Pipe, PipeTransform } from '@angular/core';
import { BazisConfigurationService } from '@bazis/configuration.service';
/**
 * Maps an enum value to a human-readable label using schemas loaded by
 * `BazisConfigurationService`. Use this pipe to display labels for enum
 * attributes defined in your API schemas.
 *
 * How it works:
 * - Looks up the attribute schema for the given entity `type` and `property`
 * - Reads `enumDict` from the schema and returns the label for `enumValue`
 * - Returns `null` when the value is empty or not found
 *
 * Dependencies:
 * - `BazisConfigurationService` (injected via `inject()`). Make sure schemas are
 *   loaded at app start by calling `BazisConfigurationService.loadConfiguration()`.
 *   The pipe expects a key in `schemas` like `${type.replace('.', '__')}__schema_retrieve`.
 *
 * Signature:
 * `transform(enumValue: string, type: string, property: string): string | null`
 *
 * Parameters:
 * - `enumValue`: Internal enum key from API (e.g., `'active'`).
 * - `type`: Entity type in dot notation (e.g., `'user.user'`).
 * - `property`: Attribute name in the schema (e.g., `'state'`).
 *
 * Examples:
 * ```html
 * <!-- Single value: cache with @let, then guard with @if -->
 * @let stateLabel = (state | enum:'user.user':'state');
 * @if (stateLabel) {
 *   {{ stateLabel }}
 * } @else {
 *   <span>-</span>
 * }
 *
 * <!-- In a list -->
 * @for (item of users; track item.id) {
 *   @let stateLabel = (item.$snapshot.state | enum:'user.user':'status');
 *   <div>{{ item.$snapshot.name }} — {{ stateLabel || '-' }}</div>
 * }
 *
 * <!-- Defensive: empty value returns null -->
 * @let label = ('' | enum:'user.user':'status');
 * @if (label) {
 *   {{ label }}
 * } @else {
 *   <span>-</span>
 * }
 * ```
 */
@Pipe({
    name: 'enum',
    standalone: true,
})
export class EnumPipe implements PipeTransform {
    protected configurationService = inject(BazisConfigurationService);

    /**
     * Convert an enum value to a readable label using schema `enumDict`.
     *
     * @param enumValue - Internal enum value from API.
     * @param type - Entity type in dot notation (e.g., 'user.user').
     * @param property - Attribute name (e.g., 'status').
     * @returns Label string or null when not found/empty.
     */
    transform(enumValue: string, type: string, property: string) {
        type = type.replace('.', '__');
        const attribute =
            this.configurationService.schemas[`${type}__schema_retrieve`].attributes[property];
        return enumValue ? attribute.enumDict[enumValue] : null;
    }
}
